"use client";

import * as React from "react";

/**
 * 3컬럼 셸 공용 프리미티브 (JCG-2138).
 *
 * ArticleShell / IndexShell / ForumProblemShell / TestCenterShell 네 셸이 각자 복붙해 들고 있던
 * `clamp` · `writeLS` · LS 폭 복원 effect 를 한 곳으로 모은다.
 *
 * **불변 계약(절대 변경 금지)**
 * - 각 셸의 LS 키 · 기본폭 · min/max 는 호출부 값을 그대로 주입한다(ui2-* / ui2-index-* /
 *   ui2-tc-* / ui2-forum-*). 프리미티브는 값을 통합하거나 바꾸지 않는다.
 * - hydration: 서버는 기본폭으로 렌더하고 마운트 후 effect 에서 LS 값을 복원한다. lazy useState
 *   init 으로 LS 를 읽으면 서버(기본값)↔클라(저장값) 그리드 인라인 스타일이 어긋나 hydration
 *   mismatch 가 나므로 금지(기존 4셸 주석 verbatim).
 */

/** 값을 [lo, hi] 로 반올림·클램프 (네 셸 공통 — 시그니처·동작 verbatim). 픽셀 폭/높이 전용. */
export const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, Math.round(v)));

/**
 * 값을 [lo, hi] 로 클램프하되 **반올림하지 않는다** — 0~1 분수 비율용 (JCG-2156).
 * `clamp` 은 Math.round 가 들어 있어 픽셀 전용이다. 분수 비율(예: 풀이/오답노트 세로 분할
 * `splitRatio`)에 `clamp` 을 쓰면 Math.round 가 0/1 로 양자화해 두 값 토글로 붕괴하므로
 * (핸들을 한 방향으로 올린 뒤 되돌릴 수 없는 버그) 비율에는 반드시 이 헬퍼를 쓴다.
 */
export const clampRatio = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

/**
 * localStorage 에 문자열로 저장한 분수 비율을 안전하게 복원한다.
 *
 * `Number(null)`/`Number("")` 는 0이라 저장값 없음이 유효한 숫자로 오인될 수 있으므로 먼저
 * 제외하고, NaN/Infinity 및 호출부의 허용 범위를 벗어난 값은 무시한다. 유효한 값만 그대로
 * 반환해 이미 검증된 사용자 조절값을 임의로 clamp 하지 않는다.
 */
export function parseStoredRatio(
  raw: string | null,
  min: number,
  max: number,
): number | null {
  if (raw === null || raw.trim() === "") return null;

  const value = Number(raw);
  if (!Number.isFinite(value) || value < min || value > max) return null;

  return value;
}

export interface SolutionSplitPreferenceRestore {
  /** null이면 현재 문제의 동적 기본값이 ratio를 결정해야 한다. */
  ratio: number | null;
  /** true면 이후 문제 전환의 0.5/0.1 기본값 요청을 무시한다. */
  userAdjusted: boolean;
}

/**
 * 오답노트 풀이 분할값의 경로 전환/마운트 복원 규칙(JCG-2856).
 *
 * 오답노트에서는 유효한 저장값만 사용자 값으로 복원하고, 저장값이 없으면 현재 문제의 풀이 유무가
 * 동적 기본값을 정하도록 ratio=null을 반환한다. 다른 경로에서는 전용 상태 누수를 막기 위해 0.5와
 * 미조절 상태로 초기화한다.
 */
export function restoreSolutionSplitPreference(
  isErrorNotesPath: boolean,
  raw: string | null,
  min: number,
  max: number,
): SolutionSplitPreferenceRestore {
  if (!isErrorNotesPath) return { ratio: 0.5, userAdjusted: false };

  const ratio = parseStoredRatio(raw, min, max);
  return { ratio, userAdjusted: ratio !== null };
}

/**
 * ProblemAnswer의 문제별 분할 기본값 요청을 실제 state update로 전달할지 결정한다.
 * 오답노트에서 사용자 값이 이미 조절/복원된 경우 null을 반환해 현재 비율을 유지한다.
 */
export function resolveProblemSplitRatioUpdate(
  requestedRatio: number,
  isErrorNotesPath: boolean,
  userAdjusted: boolean,
): number | null {
  return isErrorNotesPath && userAdjusted ? null : requestedRatio;
}

/** localStorage 안전 기록 (QuotaExceeded/비활성 등 예외 무시 — 네 셸 공통). */
export const writeLS = (key: string, value: string | number) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* ignore */
  }
};

/**
 * 마운트 후 한 번만 실행되는 복원 effect 헬퍼.
 *
 * 서버 기본값으로 렌더한 뒤 클라이언트 마운트 직후 localStorage 등에서 상태를 복원하는 패턴을
 * 캡슐화한다. lazy useState init(서버↔클라 mismatch)을 피하기 위한 표준 경로다. `restore` 안의
 * setState 는 마운트 1회 한정이라 의도적이므로 set-state-in-effect lint 를 이 헬퍼에서 끈다.
 *
 * deps 를 주면(예: ArticleShell 의 variant) 해당 값 변화 시 복원을 재실행한다(소프트 내비 대응).
 * 호출부에서 useCallback 등으로 restore 의 안정성을 관리한다.
 */
export function useRestoreOnMount(
  restore: () => void,
  deps: React.DependencyList = [],
) {
  React.useEffect(() => {
    // restore 내부의 마운트 1회 setState 는 의도된 복원이다(서버↔클라 mismatch 회피용). 함수
    // 호출로 감싸 set-state-in-effect lint 대상이 아니며, restore 안정성은 호출부 책임이다.
    restore();
    // deps 변화 시(variant 전환 등) 재복원. restore 는 매 렌더 새 함수일 수 있어 deps 에서 제외.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * localStorage 영속 폭 상태 훅.
 *
 * - 서버/첫 렌더는 `defaultWidth` 로 렌더(상수라 mismatch 없음).
 * - 마운트 후 effect 에서 `key` 의 LS 값을 읽어 clamp 복원(lazy init 금지 계약).
 * - 반환 setter `setWidthPersisted(px)` 는 clamp 후 state 갱신 + LS 기록을 한 번에 한다.
 *   ResizeHandle 의 `onResize: (px) => void` 와 그대로 호환된다.
 *
 * 단일 폭 + 고정 키·min/max 인 셸 폭에 쓴다(IndexShell 좌/우, TestCenterShell 우, ForumProblemShell
 * 좌). 모드별로 키/clamp 가 달라지는 복합 폭(ArticleShell 의 right/solution/compose,
 * ForumProblemShell 의 solution)은 호출부에서 clamp/writeLS 로 직접 처리한다.
 */
export function usePersistedWidth(
  key: string,
  defaultWidth: number,
  min: number,
  max: number,
): [number, (px: number) => void] {
  const [width, setWidth] = React.useState(defaultWidth);

  useRestoreOnMount(() => {
    try {
      const v = Number(localStorage.getItem(key));
      if (v) setWidth(clamp(v, min, max));
    } catch {
      /* ignore */
    }
  });

  const setWidthPersisted = React.useCallback(
    (px: number) => {
      const v = clamp(px, min, max);
      setWidth(v);
      writeLS(key, v);
    },
    [key, min, max],
  );

  return [width, setWidthPersisted];
}

/**
 * 컴팩트 셸 우측 폭 캡 (JCG-2224) — 레일 58 + 우측 420 기준 1024px에서 본문 ~546px.
 * ArticleShell(풀이/작성/패널)·ForumProblemShell(풀이) 우측 폭이 공유하는 상한.
 */
export const COMPACT_RIGHT_MAX = 420;

/**
 * 컴팩트 셸 구간에서만 우측 폭을 `COMPACT_RIGHT_MAX` 로 캡한다(영속값 불변, 렌더 시 유효 폭만).
 * compact=false(1280px↑·SSR)면 px 를 그대로 통과시켜 기존 경험을 건드리지 않는다.
 */
export const capCompactWidth = (px: number, compact: boolean) =>
  compact ? Math.min(px, COMPACT_RIGHT_MAX) : px;

/**
 * 컴팩트 셸(1024~1279, JCG-2224) 진입/이탈 시 좌측 사이드바 자동 접힘.
 * compact "변화"시에만 동작(같은 구간 내 사용자 토글은 덮어쓰지 않음).
 * 풀이 모드와 독립적으로 뷰포트 구간 변화만 소유한다.
 */
export function useCompactAutoCollapse(
  compact: boolean,
  setLeftCollapsed: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const prevCompactRef = React.useRef<boolean | null>(null);
  React.useEffect(() => {
    if (prevCompactRef.current === compact) return;
    const isFirstRun = prevCompactRef.current === null;
    prevCompactRef.current = compact;
    if (compact) {
      setLeftCollapsed(true);
    } else if (!isFirstRun) {
      setLeftCollapsed(false);
    }
  }, [compact, setLeftCollapsed]);
}
